/**
 * Curated static URL → title/description for high-value indexable paths.
 * See AGENTS.md (SEO title & meta description) for the copy formula.
 *
 * Titles follow: {primary keywords} | {intent or value} | JobCrawls
 * Descriptions: keyword echo + aggregation value + freshness + soft CTA (~145–160 chars).
 *
 * IMPORTANT: a large share of the keys below are DEAD CODE on production. CloudFront routes
 * `/`, `/en`, `/fi`, `/sv`, the about/privacy/terms/partnerships pages, and the entire resources
 * hub (`/en/resources*`, `/fi/tyoelama*`, `/sv/arbetsliv*`) straight to the web-static S3
 * prerender (see `web-app/cdk/lib/vike-stack.ts`'s `addBehavior(..., assetOrigin, ...)` calls,
 * around line 1682 onward) — this file's `getStaticUrlSeoOverride` lookup, called from
 * `web-app/pages/index/+title.ts`, never runs for those paths, so editing their entries here has
 * no effect on the live site. Their real, working titles live in web-static instead, one
 * `+title.ts` per page directory (e.g. `web-static/pages/landing/seo-meta.ts` for the homepages,
 * `web-static/pages/tyoelama/+title.ts` for the fi resource hub). Verified live 2026-09-04 by
 * curling production and diffing against both sources.
 *
 * The entries that ARE live (still routed to the web-app Lambda/SSR origin, so this file is the
 * real source of truth): location SRP pages (`/en/jobs/location/...` and fi/sv equivalents), the
 * two curated role pages, `/en/locations` + fi/sv, and `/en/employers` + fi/sv. `/en/for-employers`
 * + fi/sv is NOT live from here despite the entry below existing in this file — that page owns its
 * own title/description in `web-app/pages/for-employers/+title.ts` and `+Head.tsx`, which never
 * read this file. When adding a new key here, check the CDK behavior list first — if the exact
 * path (or a matching wildcard) is routed to `assetOrigin`, this file is the wrong place for it.
 */

export interface UrlStaticSeoMeta {
  title: string;
  description: string;
}

/** Curated copy for the English marketing home (`/` and legacy `/en` lookup). */
const EN_MARKETING_HOME_SEO: UrlStaticSeoMeta = {
  title: "Job Search in Finland | Track Every Application | JobCrawls",
  description:
    "JobCrawls aggregates jobs from Finnish and international boards in one search and lets you track every application in one place. Built for serious job seekers, not recruiters.",
};

/** Path keys are pathname-only (no domain), no trailing slash; `/` and `/en` use the same English home row. */
export const STATIC_URL_SEO_META: Record<string, UrlStaticSeoMeta> = {

  // ---------------------------------------------------------------------------
  // HOMEPAGES
  // ---------------------------------------------------------------------------

  "/": EN_MARKETING_HOME_SEO,

  "/en": EN_MARKETING_HOME_SEO,

  "/fi": {
    title: "Työnhaku yhdellä haulla | Hakemusten seuranta | JobCrawls",
    description:
      "JobCrawls kokoaa avoimet työpaikat yritysten urasivuilta, Työmarkkinatorilta ja kansainvälisistä lähteistä yhteen hakuun. Seuraa hakemuksesi samassa palvelussa. Tehty vakavaan työnhakuun.",
  },

  "/sv": {
    title: "Jobbsök i Finland | Följ dina ansökningar | JobCrawls",
    description:
      "JobCrawls samlar jobb från finska och internationella jobbsajter i en sökning och låter dig följa alla ansökningar på ett ställe. Byggt för seriösa jobbsökare, inte rekryterare.",
  },

  // ---------------------------------------------------------------------------
  // LOCATION PAGES
  // ---------------------------------------------------------------------------

  "/en/jobs/location/helsinki_finland": {
    title: "Jobs in Helsinki | Live Listings, One Search | JobCrawls",
    description:
      "Find Helsinki jobs from company career pages and Työmarkkinatori in one search. Roles in tech, healthcare and finance, updated daily.",
  },

  "/fi/tyopaikat/alue/helsinki_suomi": {
    title: "Avoimet työpaikat Helsinki | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Helsingin avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä teknologiassa, terveydenhuollossa ja rahoituksessa.",
  },

  "/sv/lediga-jobb/omrade/helsingfors_finland": {
    title: "Lediga jobb Helsingfors | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Helsingfors från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom teknik, hälsovård och finans, uppdateras dagligen.",
  },

  "/en/jobs/location/espoo_finland": {
    title: "Jobs in Espoo | Live Listings, One Search | JobCrawls",
    description:
      "Find Espoo jobs from company career pages and Työmarkkinatori in one search. Roles in tech, engineering and healthcare, updated daily.",
  },

  "/fi/tyopaikat/alue/espoo_suomi": {
    title: "Avoimet työpaikat Espoo | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Espoon avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/esbo_finland": {
    title: "Lediga jobb Esbo | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Esbo från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/tampere_finland": {
    title: "Jobs in Tampere | Live Listings, One Search | JobCrawls",
    description:
      "Find Tampere jobs from company career pages and Työmarkkinatori in one search. Roles in engineering, manufacturing and tech, updated daily.",
  },

  "/fi/tyopaikat/alue/tampere_suomi": {
    title: "Avoimet työpaikat Tampere | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Tampereen avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/tammerfors_finland": {
    title: "Lediga jobb Tammerfors | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Tammerfors från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/vantaa_finland": {
    title: "Jobs in Vantaa | Live Listings, One Search | JobCrawls",
    description:
      "Find Vantaa jobs from company career pages and Työmarkkinatori in one search. Roles in logistics, aviation and tech, updated daily.",
  },

  "/fi/tyopaikat/alue/vantaa_suomi": {
    title: "Avoimet työpaikat Vantaa | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Vantaan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/vanda_finland": {
    title: "Lediga jobb Vanda | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Vanda från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/turku_finland": {
    title: "Jobs in Turku | Live Listings, One Search | JobCrawls",
    description:
      "Find Turku jobs from company career pages and Työmarkkinatori in one search. Roles in maritime, pharma and tech, updated daily.",
  },

  "/fi/tyopaikat/alue/turku_suomi": {
    title: "Avoimet työpaikat Turku | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Turun avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  // Keys below use the percent-encoded pathname (`new URL(...).pathname` form); the
  // previous ASCII-folded keys (Abo, Uleaborg, Jyvaskyla, Etatyo) never matched and
  // these pages silently fell back to generated meta. See handoff note further down.
  "/sv/lediga-jobb/omrade/%c3%85bo_finland": {
    title: "Lediga jobb Åbo | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Åbo från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/oulu_finland": {
    title: "Jobs in Oulu | Live Listings, One Search | JobCrawls",
    description:
      "Find Oulu jobs from company career pages and Työmarkkinatori in one search. Roles in tech, telecom and healthcare, updated daily.",
  },

  "/fi/tyopaikat/alue/oulu_suomi": {
    title: "Avoimet työpaikat Oulu | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Oulun avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/ule%c3%a5borg_finland": {
    title: "Lediga jobb Uleåborg | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Uleåborg från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/jyv%c3%a4skyl%c3%a4_finland": {
    title: "Jobs in Jyväskylä | Live Listings, One Search | JobCrawls",
    description:
      "Find Jyväskylä jobs from company career pages and Työmarkkinatori in one search. Roles in education, tech and logistics, updated daily.",
  },

  "/fi/tyopaikat/alue/jyv%c3%a4skyl%c3%a4_suomi": {
    title: "Avoimet työpaikat Jyväskylä | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Jyväskylän avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/jyv%c3%a4skyl%c3%a4_finland": {
    title: "Lediga jobb Jyväskylä | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Jyväskylä från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/lahti_finland": {
    title: "Jobs in Lahti | Live Listings, One Search | JobCrawls",
    description:
      "Find Lahti jobs from company career pages and Työmarkkinatori in one search. Roles in manufacturing, logistics and tech, updated daily.",
  },

  "/fi/tyopaikat/alue/lahti_suomi": {
    title: "Avoimet työpaikat Lahti | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Lahden avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/lahtis_finland": {
    title: "Lediga jobb Lahtis | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Lahtis från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/kuopio_finland": {
    title: "Jobs in Kuopio | Live Listings, One Search | JobCrawls",
    description:
      "Find Kuopio jobs from company career pages and Työmarkkinatori in one search. Roles in healthcare, tech and education, updated daily.",
  },

  "/fi/tyopaikat/alue/kuopio_suomi": {
    title: "Avoimet työpaikat Kuopio | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Kuopion avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/kuopio_finland": {
    title: "Lediga jobb Kuopio | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Kuopio från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/pori_finland": {
    title: "Jobs in Pori | Live Listings, One Search | JobCrawls",
    description:
      "Find Pori jobs from company career pages and Työmarkkinatori in one search. Roles in manufacturing, energy and tech, updated daily.",
  },

  "/fi/tyopaikat/alue/pori_suomi": {
    title: "Avoimet työpaikat Pori | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Porin avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  // Percent-encoded key: the site self-canonicalises Pori to Björneborg on /sv (see
  // seo/srpAllowlist.ts); the old ASCII-folded "Bjorneborg_Finland" key never matched.
  "/sv/lediga-jobb/omrade/bj%c3%b6rneborg_finland": {
    title: "Lediga jobb Björneborg | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Björneborg från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  "/en/jobs/location/kotka_finland": {
    title: "Jobs in Kotka | Live Listings, One Search | JobCrawls",
    description:
      "Find Kotka jobs from company career pages and Työmarkkinatori in one search. Roles in logistics, maritime and manufacturing, updated daily.",
  },

  "/fi/tyopaikat/alue/kotka_suomi": {
    title: "Avoimet työpaikat Kotka | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Kotkan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/omrade/kotka_finland": {
    title: "Lediga jobb Kotka | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Kotka från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.",
  },

  // ---------------------------------------------------------------------------
  // Second wave of city SRPs, paired with the matching hubs in seo/srpAllowlist.ts.
  //
  // Two things to keep in mind when extending this block:
  //  1. Keys are matched against `new URL(...).pathname`, which is ALWAYS
  //     percent-encoded and is never ASCII-folded. So Seinajoki/Hameenlinna keys
  //     must be written as %C3%A4, not as a bare "a". (The older Jyvaskyla / Abo /
  //     Uleaborg / Etatyo keys above are ASCII-folded and therefore never match --
  //     those pages silently fall back to generated meta. See handoff notes.)
  //  2. The `sv` path uses whatever slug the site self-canonicalises to, which for
  //     Porvoo/Rauma/Kokkola is still the Finnish name even though the page renders
  //     "Borga"/"Raumo"/"Karleby". The copy uses the Swedish name to match the H1.
  // ---------------------------------------------------------------------------

  "/en/jobs/location/rovaniemi_finland": {
    title: "Jobs in Rovaniemi | Live Listings, One Search | JobCrawls",
    description:
      "Find Rovaniemi jobs from company career pages and Työmarkkinatori in one search. Roles in tourism, healthcare and Arctic research, updated daily.",
  },

  "/fi/tyopaikat/alue/rovaniemi_suomi": {
    title: "Avoimet työpaikat Rovaniemi | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Rovaniemen avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä matkailussa, hoitoalalla ja tutkimuksessa.",
  },

  "/sv/lediga-jobb/omrade/rovaniemi_finland": {
    title: "Lediga jobb Rovaniemi | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Rovaniemi från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom turism, vård och forskning, uppdateras dagligen.",
  },

  "/en/jobs/location/vaasa_finland": {
    title: "Jobs in Vaasa | Live Listings, One Search | JobCrawls",
    description:
      "Find Vaasa jobs from company career pages and Työmarkkinatori in one search. Roles in energy technology, engineering and manufacturing, updated daily.",
  },

  "/fi/tyopaikat/alue/vaasa_suomi": {
    title: "Avoimet työpaikat Vaasa | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Vaasan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä energiatekniikassa, tekniikassa ja teollisuudessa.",
  },

  "/sv/lediga-jobb/omrade/vasa_finland": {
    title: "Lediga jobb Vasa | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Vasa från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom energiteknik, teknik och industri, uppdateras dagligen.",
  },

  "/en/jobs/location/kouvola_finland": {
    title: "Jobs in Kouvola | Live Listings, One Search | JobCrawls",
    description:
      "Find Kouvola jobs from company career pages and Työmarkkinatori in one search. Roles in logistics, transport and manufacturing, updated daily.",
  },

  "/fi/tyopaikat/alue/kouvola_suomi": {
    title: "Avoimet työpaikat Kouvola | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Kouvolan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä logistiikassa, kuljetuksessa ja teollisuudessa.",
  },

  "/sv/lediga-jobb/omrade/kouvola_finland": {
    title: "Lediga jobb Kouvola | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Kouvola från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom logistik, transport och industri, uppdateras dagligen.",
  },

  "/en/jobs/location/sein%c3%a4joki_finland": {
    title: "Jobs in Seinäjoki | Live Listings, One Search | JobCrawls",
    description:
      "Find Seinäjoki jobs from company career pages and Työmarkkinatori in one search. Roles in food industry, agriculture and manufacturing, updated daily.",
  },

  "/fi/tyopaikat/alue/sein%c3%a4joki_suomi": {
    title: "Avoimet työpaikat Seinäjoki | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Seinäjoen avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä elintarvikealalla, maataloudessa ja teollisuudessa.",
  },

  "/sv/lediga-jobb/omrade/sein%c3%a4joki_finland": {
    title: "Lediga jobb Seinäjoki | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Seinäjoki från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom livsmedel, jordbruk och industri, uppdateras dagligen.",
  },

  "/en/jobs/location/h%c3%a4meenlinna_finland": {
    title: "Jobs in Hämeenlinna | Live Listings, One Search | JobCrawls",
    description:
      "Find Hämeenlinna jobs from company career pages and Työmarkkinatori in one search. Roles in manufacturing, healthcare and logistics, updated daily.",
  },

  "/fi/tyopaikat/alue/h%c3%a4meenlinna_suomi": {
    title: "Avoimet työpaikat Hämeenlinna | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Hämeenlinnan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä teollisuudessa, hoitoalalla ja logistiikassa.",
  },

  "/sv/lediga-jobb/omrade/tavastehus_finland": {
    title: "Lediga jobb Tavastehus | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Tavastehus från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom industri, vård och logistik, uppdateras dagligen.",
  },

  "/en/jobs/location/porvoo_finland": {
    title: "Jobs in Porvoo | Live Listings, One Search | JobCrawls",
    description:
      "Find Porvoo jobs from company career pages and Työmarkkinatori in one search. Roles in energy, refining, tourism and services, updated daily.",
  },

  "/fi/tyopaikat/alue/porvoo_suomi": {
    title: "Avoimet työpaikat Porvoo | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Porvoon avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä energia-alalla, teollisuudessa ja palveluissa.",
  },

  "/sv/lediga-jobb/omrade/porvoo_finland": {
    title: "Lediga jobb Borgå | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Borgå från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom energi, industri och service, uppdateras dagligen.",
  },

  "/en/jobs/location/mikkeli_finland": {
    title: "Jobs in Mikkeli | Live Listings, One Search | JobCrawls",
    description:
      "Find Mikkeli jobs from company career pages and Työmarkkinatori in one search. Roles in healthcare, education and defence technology, updated daily.",
  },

  "/fi/tyopaikat/alue/mikkeli_suomi": {
    title: "Avoimet työpaikat Mikkeli | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Mikkelin avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä hoitoalalla, koulutuksessa ja teknologiassa.",
  },

  "/sv/lediga-jobb/omrade/st_michel_finland": {
    title: "Lediga jobb S:t Michel | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i S:t Michel från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom vård, utbildning och teknik, uppdateras dagligen.",
  },

  "/en/jobs/location/lappeenranta_finland": {
    title: "Jobs in Lappeenranta | Live Listings, One Search | JobCrawls",
    description:
      "Find Lappeenranta jobs from company career pages and Työmarkkinatori in one search. Roles in engineering, clean technology and forestry, updated daily.",
  },

  "/fi/tyopaikat/alue/lappeenranta_suomi": {
    title: "Avoimet työpaikat Lappeenranta | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Lappeenrannan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä tekniikassa, cleantechissä ja metsäalalla.",
  },

  "/sv/lediga-jobb/omrade/villmanstrand_finland": {
    title: "Lediga jobb Villmanstrand | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Villmanstrand från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom teknik, cleantech och skog, uppdateras dagligen.",
  },

  "/en/jobs/location/rauma_finland": {
    title: "Jobs in Rauma | Live Listings, One Search | JobCrawls",
    description:
      "Find Rauma jobs from company career pages and Työmarkkinatori in one search. Roles in marine industry, manufacturing and logistics, updated daily.",
  },

  "/fi/tyopaikat/alue/rauma_suomi": {
    title: "Avoimet työpaikat Rauma | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Rauman avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä meriteollisuudessa, valmistuksessa ja logistiikassa.",
  },

  "/sv/lediga-jobb/omrade/rauma_finland": {
    title: "Lediga jobb Raumo | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Raumo från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom marinindustri, tillverkning och logistik, uppdateras dagligen.",
  },

  "/en/jobs/location/kokkola_finland": {
    title: "Jobs in Kokkola | Live Listings, One Search | JobCrawls",
    description:
      "Find Kokkola jobs from company career pages and Työmarkkinatori in one search. Roles in chemicals, industry, logistics and cleantech, updated daily.",
  },

  "/fi/tyopaikat/alue/kokkola_suomi": {
    title: "Avoimet työpaikat Kokkola | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Kokkolan avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä kemianteollisuudessa, teollisuudessa ja logistiikassa.",
  },

  "/sv/lediga-jobb/omrade/kokkola_finland": {
    title: "Lediga jobb Karleby | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Karleby från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom kemi, industri och logistik, uppdateras dagligen.",
  },

  "/en/jobs/location/kemi_finland": {
    title: "Jobs in Kemi | Live Listings, One Search | JobCrawls",
    description:
      "Find Kemi jobs from company career pages and Työmarkkinatori in one search. Roles in forest industry, manufacturing and port logistics, updated daily.",
  },

  "/fi/tyopaikat/alue/kemi_suomi": {
    title: "Avoimet työpaikat Kemi | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Kemin avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä metsäteollisuudessa, valmistuksessa ja logistiikassa.",
  },

  "/sv/lediga-jobb/omrade/kemi_finland": {
    title: "Lediga jobb Kemi | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Kemi från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom skogsindustri, tillverkning och logistik, uppdateras dagligen.",
  },

  // Third wave (Aug 2026): Joensuu reinstated after inventory grew 31 → 69,
  // Salo newly added (145 active listings). Both names are ASCII in all three
  // locales, so no percent-encoding concerns for these keys.

  "/en/jobs/location/joensuu_finland": {
    title: "Jobs in Joensuu | Live Listings, One Search | JobCrawls",
    description:
      "Find Joensuu jobs from company career pages and Työmarkkinatori in one search. Roles in forestry, education and research, updated daily.",
  },

  "/fi/tyopaikat/alue/joensuu_suomi": {
    title: "Avoimet työpaikat Joensuu | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Joensuun avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä metsäalalla, koulutuksessa ja tutkimuksessa.",
  },

  "/sv/lediga-jobb/omrade/joensuu_finland": {
    title: "Lediga jobb Joensuu | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Joensuu från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom skogsbruk, utbildning och forskning, uppdateras dagligen.",
  },

  "/en/jobs/location/salo_finland": {
    title: "Jobs in Salo | Live Listings, One Search | JobCrawls",
    description:
      "Find Salo jobs from company career pages and Työmarkkinatori in one search. Roles in technology, manufacturing and services, updated daily.",
  },

  "/fi/tyopaikat/alue/salo_suomi": {
    title: "Avoimet työpaikat Salo | Yhdellä haulla | JobCrawls",
    description:
      "Löydä Salon avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Tehtäviä teknologiassa, teollisuudessa ja palveluissa.",
  },

  "/sv/lediga-jobb/omrade/salo_finland": {
    title: "Lediga jobb Salo | Allt i en sökning | JobCrawls",
    description:
      "Hitta lediga jobb i Salo från företags karriärsidor och Työmarkkinatori i en sökning. Roller inom teknik, tillverkning och service, uppdateras dagligen.",
  },

  // ---------------------------------------------------------------------------
  // ROLE PAGES (Aug 2026 search report, cluster 6: healthcare only)
  // ---------------------------------------------------------------------------

  "/en/jobs/role/lahihoitaja": {
    // Two segments, matching fi/sv below and the company-page precedent documented in
    // AGENTS.md: the role name is the query, so it doesn't need a second differentiating segment.
    title: "Practical Nurse Jobs in Finland | JobCrawls",
    description:
      "Find practical nurse (lähihoitaja) jobs from company career pages and Työmarkkinatori in one search. Elderly care, home care and disability services, updated daily.",
  },

  "/fi/tyopaikat/ammatti/lahihoitaja": {
    title: "Avoimet lähihoitajan työpaikat Suomessa | JobCrawls",
    description:
      "Löydä avoimet lähihoitajan työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Paikkoja vanhustyössä, kotihoidossa ja vammaispalveluissa. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/yrke/lahihoitaja": {
    title: "Lediga närvårdarjobb i Finland | JobCrawls",
    description:
      "Hitta lediga jobb som närvårdare från företags karriärsidor och Työmarkkinatori i en sökning. Äldreomsorg, hemvård och handikappservice, uppdateras dagligen.",
  },

  "/en/jobs/role/sairaanhoitaja": {
    // Two segments, matching fi/sv below (see the /lahihoitaja entry above for why).
    title: "Registered Nurse Jobs in Finland | JobCrawls",
    description:
      "Find registered nurse (sairaanhoitaja) jobs from company career pages and Työmarkkinatori in one search. Hospital, clinic and home care nursing, updated daily.",
  },

  "/fi/tyopaikat/ammatti/sairaanhoitaja": {
    title: "Avoimet sairaanhoitajan työpaikat Suomessa | JobCrawls",
    description:
      "Löydä avoimet sairaanhoitajan työpaikat yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Paikkoja sairaaloissa, terveysasemilla ja kotisairaanhoidossa. Päivitetty päivittäin.",
  },

  "/sv/lediga-jobb/yrke/sairaanhoitaja": {
    title: "Lediga sjuksköterskejobb i Finland | JobCrawls",
    description:
      "Hitta lediga sjuksköterskejobb från företags karriärsidor och Työmarkkinatori i en sökning. Sjukhus, hälsostationer och hemsjukvård, uppdateras dagligen.",
  },

  "/en/jobs/location/remote_finland": {
    title: "Remote Jobs in Finland | Track Your Applications | JobCrawls",
    description:
      "Search company career pages and Työmarkkinatori for remote roles in Finland in one place. Track every application as you go.",
  },

  "/fi/tyopaikat/alue/et%c3%a4ty%c3%b6_suomi": {
    title: "Etätyömahdollisuudet Suomessa | Avoimet etätyöt | JobCrawls",
    description:
      "Löydä avoimet etätyömahdollisuudet Suomessa: etätyöt yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin, seuraa hakemuksesi samassa palvelussa.",
  },

  "/sv/lediga-jobb/omrade/distans_finland": {
    title: "Distansjobb i Finland | Allt i en sökning | JobCrawls",
    description:
      "Sök distansjobb från företags karriärsidor och Työmarkkinatori i en sökning. Följ dina ansökningar på samma ställe.",
  },

  "/en/jobs/location/remote_europe": {
    title: "Remote Jobs in Europe | For Finland Seekers | JobCrawls",
    description:
      "European remote roles alongside Finnish listings, aggregated in one search. Track every application in one place.",
  },

  "/fi/tyopaikat/alue/et%c3%a4ty%c3%b6_eurooppa": {
    title: "Etätyömahdollisuudet Eurooppa | Suomesta haettavat | JobCrawls",
    description:
      "JobCrawls näyttää eurooppalaiset etätyömahdollisuudet suomalaisten ilmoitusten rinnalla. Seuraa hakemuksesi samassa palvelussa.",
  },

  "/sv/lediga-jobb/omrade/distans_europa": {
    title: "Distansjobb Europa | För jobbsökare i Finland | JobCrawls",
    description:
      "JobCrawls visar europeiska distansjobb bredvid finska annonser i en sökning. Följ alla ansökningar på ett ställe.",
  },

  // ---------------------------------------------------------------------------
  // STATIC PAGES
  // ---------------------------------------------------------------------------

  "/en/locations": {
    title: "Job Locations in Finland | Browse by City | JobCrawls",
    description:
      "Browse open jobs by city across Finland. Helsinki, Tampere, Turku, Oulu and every major Finnish city covered with live listings aggregated from company career pages and Työmarkkinatori, updated daily.",
  },

  "/fi/sijainnit": {
    title: "Työpaikkojen sijainnit Suomessa | Selaa kaupungeittain | JobCrawls",
    description:
      "Selaa avoimia työpaikkoja kaupungeittain ympäri Suomen. Helsinki, Tampere, Turku, Oulu ja kaikki suurimmat kaupungit katettu tuoreilla ilmoituksilla yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/platser": {
    title: "Jobborter i Finland | Bläddra efter stad | JobCrawls",
    description:
      "Bläddra bland lediga jobb stad för stad i hela Finland. Helsingfors, Tammerfors, Åbo och Uleåborg med dagliga uppdateringar från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/employers": {
    title: "Best Companies to Work for in Finland | Ranked by Hiring Activity | JobCrawls",
    description:
      "Discover Finland's top employers ranked by active hiring and employee reputation. Compare companies across tech, healthcare, engineering and finance. Find where you actually want to work.",
  },

  "/fi/tyonantajat": {
    title: "Parhaat yritykset Suomessa | Rekrytoivat aktiivisesti nyt | JobCrawls",
    description:
      "Tutustu Suomen arvostetuimpiin työnantajiin rekrytointiaktiivisuuden ja työntekijämaineen perusteella. Vertaile yrityksiä teknologiassa, terveydenhuollossa ja insinöörityössä.",
  },

  "/sv/alla-arbetsgivare": {
    title: "Bästa företagen i Finland | Rankade efter rekryteringsaktivitet | JobCrawls",
    description:
      "Utforska Finlands främsta arbetsgivare rankade efter aktiv rekrytering och anseende. Jämför företag inom teknik, hälso- och sjukvård, ingenjörstjänster och finans.",
  },

  "/en/for-employers": {
    title: "For Employers | Reach Finland's Most Active Job Seekers | JobCrawls",
    description:
      "JobCrawls puts your roles in front of job seekers searching company career pages and Työmarkkinatori: reach Finland's most active candidates.",
  },

  "/fi/tyonantajille": {
    title: "Työnantajille | Tavoita aktiivisimmat työnhakijat Suomessa | JobCrawls",
    description:
      "JobCrawls kokoaa työnhakijat, jotka etsivät töitä yritysten urasivuilta ja Työmarkkinatorilta yhdessä paikassa. Opi miten saat ilmoituksesi Suomen aktiivisimpien työnhakijoiden näkyviin.",
  },

  "/sv/for-arbetsgivare": {
    title: "För arbetsgivare | Nå Finlands mest aktiva jobbsökare | JobCrawls",
    description:
      "JobCrawls aggregerar jobbsökare som söker på företags karriärsidor och Työmarkkinatori på ett ställe. Lär dig hur du får dina annonser framför Finlands mest aktiva kandidater.",
  },

  "/en/partnerships": {
    title: "Partnerships | Distribute Your Jobs Across Finland | JobCrawls",
    description:
      "Partner with JobCrawls to reach job seekers across Finland from one integration. Distribution partnerships, data partnerships and job board syndication available.",
  },

  "/fi/kumppanuudet": {
    title: "Kumppanuudet | Jaa työpaikkasi kaikille suomalaisille alustoille | JobCrawls",
    description:
      "Tee yhteistyötä JobCrawlsin kanssa ja tavoita työnhakijat ympäri Suomen yhden integraation kautta. Jakeluyhteistyö, datayhteistyö ja syndikointi.",
  },

  "/sv/partnerskap": {
    title: "Partnerskap | Distribuera dina jobb till hela Finland | JobCrawls",
    description:
      "Samarbeta med JobCrawls för att nå jobbsökare i hela Finland från en integration. Distributionspartnerskap, datapartnerskap och jobbannonssyndikering tillgängligt.",
  },

  "/en/about": {
    title: "About JobCrawls | Built for Serious Job Seekers | JobCrawls",
    description:
      "JobCrawls exists because serious job seekers need more than one job board. We aggregate Finnish and international listings into one search and let you track every application in one place.",
  },

  "/fi/tietoa-jobcrawlsista": {
    title: "Tietoa JobCrawlsista | Rakennettu vakavaan työnhakuun | JobCrawls",
    description:
      "JobCrawls on rakennettu työnhakijoille, jotka hakevat tosissaan. Kokoamme suomalaiset ja kansainväliset työpaikat yhteen hakuun ja annamme sinulle työkalut seurata kaikkia hakemuksiasi.",
  },

  "/sv/om-jobcrawls": {
    title: "Om JobCrawls | Byggt för seriösa jobbsökare | JobCrawls",
    description:
      "JobCrawls är byggt för jobbsökare som söker på allvar. Vi aggregerar finska och internationella jobb i en sökning och låter dig följa alla dina ansökningar på ett ställe.",
  },

  "/en/privacy": {
    title: "Privacy Policy | How We Handle Your Data | JobCrawls",
    description:
      "Read JobCrawls' privacy policy. We explain clearly what data we collect, how it is used, and how you can control it. GDPR compliant and based in Finland.",
  },

  "/fi/tietosuoja": {
    title: "Tietosuojaseloste | Miten käsittelemme tietojasi | JobCrawls",
    description:
      "Lue JobCrawlsin tietosuojaseloste. Kerromme selkokielellä mitä tietoja keräämme, miten niitä käytetään ja miten voit hallita niitä. GDPR-yhteensopiva, Suomessa toimiva palvelu.",
  },

  "/sv/integritet": {
    title: "Integritetspolicy | Hur vi hanterar dina uppgifter | JobCrawls",
    description:
      "Läs JobCrawls integritetspolicy. Vi förklarar tydligt vilka uppgifter vi samlar in, hur de används och hur du kan kontrollera dem. GDPR-kompatibel tjänst baserad i Finland.",
  },

  "/en/terms": {
    title: "Terms of Service | Using JobCrawls | JobCrawls",
    description:
      "Read the terms governing your use of JobCrawls. Covers job seeker use, employer access, data usage and our aggregation of company career page and Työmarkkinatori listings.",
  },

  "/fi/kayttoehdot": {
    title: "Käyttöehdot | JobCrawlsin käyttäminen | JobCrawls",
    description:
      "Lue JobCrawlsin käyttöehdot. Kattaa työnhakijoiden käytön, työnantajien pääsyn, tietojen käytön sekä yritysten urasivujen ja Työmarkkinatorin ilmoitusten yhdistämisen.",
  },

  "/sv/villkor": {
    title: "Användningsvillkor | Att använda JobCrawls | JobCrawls",
    description:
      "Läs villkoren för din användning av JobCrawls. Täcker jobbsökares användning, arbetsgivaråtkomst, dataanvändning och vår aggregering av annonser från företags karriärsidor och Työmarkkinatori.",
  },

  // ---------------------------------------------------------------------------
  // RESOURCES HUB
  // ---------------------------------------------------------------------------

  "/en/resources": {
    title: "Job Search Guides for Finland | For Serious Job Seekers | JobCrawls",
    description:
      "Salary benchmarks, job market data, CV guides and application strategy for people running a serious job search in Finland. Written for seekers who want the full picture, not just a list of jobs.",
  },

  "/fi/tyoelama": {
    title: "Työnhakuoppaat Suomessa | Vakavaan työnhakuun | JobCrawls",
    description:
      "Palkkatietoja, työmarkkinakatsauksia, CV-vinkkejä ja hakustrategiaa työnhakijoille, jotka hakevat tosissaan. Kirjoitettu heille, jotka haluavat kokonaiskuvan pelkän ilmoituslistan sijaan.",
  },

  "/sv/arbetsliv": {
    title: "Jobbsökarguider för Finland | För seriösa jobbsökare | JobCrawls",
    description:
      "Lönedata, arbetsmarknadsanalyser, CV-tips och sökstrategi för dig som söker jobb på allvar i Finland. Skrivet för dem som vill ha hela bilden, inte bara en lista med jobb.",
  },

  // ---------------------------------------------------------------------------
  // RESOURCE ARTICLES
  // ---------------------------------------------------------------------------

  "/en/resources/remote-work-in-finland": {
    title: "Remote Work in Finland | What Job Seekers Need to Know | JobCrawls",
    description:
      "Which sectors actually hire remote in Finland, what Finnish employers expect from remote workers, and where to find the roles, all in one place instead of hopping between job boards.",
  },

  "/fi/tyoelama/etatyo-suomessa": {
    title: "Etätyö Suomessa | Mitä työnhakijan tulee tietää | JobCrawls",
    description:
      "Kaikki oleellinen etätyöstä Suomessa: millä aloilla rekrytoidaan etänä, mitä suomalaiset työnantajat odottavat ja mistä löydät parhaat etätyöpaikat yhdestä hausta.",
  },

  "/sv/arbetsliv/distansarbete-i-finland": {
    title: "Distansarbete i Finland | Vad jobbsökare behöver veta | JobCrawls",
    description:
      "Allt du behöver veta om distansarbete i Finland: vilka sektorer rekryterar på distans, vad finska arbetsgivare förväntar sig och var du hittar de bästa distansjobben i en sökning.",
  },

  "/en/resources/salary-transparency-in-finnish-job-listings": {
    title: "Salary Transparency in Finnish Job Listings | What to Expect | JobCrawls",
    description:
      "How common is salary information in Finnish job ads and what does the EU Pay Transparency Directive mean for job seekers in Finland? We analysed listings from company career pages and Työmarkkinatori.",
  },

  "/fi/tyoelama/palkkalapinakyvyys-suomalaisissa-tyopaikkailmoituksissa": {
    title: "Palkkaläpinäkyvyys suomalaisissa työpaikkailmoituksissa | JobCrawls",
    description:
      "Miten yleistä palkkatiedon ilmoittaminen on suomalaisissa työpaikkailmoituksissa ja mitä EU:n palkkaläpinäkyvyysdirektiivi tarkoittaa työnhakijalle? Analysoimme ilmoitukset yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/lonetransparens-i-finska-jobbannonser": {
    title: "Lönetransparens i finska jobbannonser | Vad du kan förvänta dig | JobCrawls",
    description:
      "Hur vanligt är det med löneinformation i finska jobbannonser och vad innebär EU:s lönetransparensdirektiv för jobbsökare i Finland? Vi analyserade annonser från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/finland-salary-transparency-law-2026": {
    title: "Finland Salary Transparency Law 2026 | What Changes for Job Seekers | JobCrawls",
    description:
      "From May 2026, Finnish employers must share salary ranges early in hiring. We break down which ones are affected, how disclosure will change, and what you can actually expect as a job seeker.",
  },

  "/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026": {
    title: "Palkkaläpinäkyvyyslaki Suomi 2026 | Mitä muuttuu työnhakijalle | JobCrawls",
    description:
      "EU:n palkkaläpinäkyvyysdirektiivi astuu voimaan 2026. Mitä se tarkoittaa työnhakijalle Suomessa, keitä työnantajia se koskee ja miten palkkatietojen ilmoittaminen muuttuu.",
  },

  "/sv/arbetsliv/lonetransparens-lag-finland-2026": {
    title: "Lönetransparenslagen Finland 2026 | Vad ändras för jobbsökare | JobCrawls",
    description:
      "EU:s lönetransparensdirektiv träder i kraft 2026. Här är vad det innebär för jobbsökare i Finland, vilka arbetsgivare berörs och hur löneredovisningen förändras på finska jobbsajter.",
  },

  "/en/resources/how-to-use-linkedin-to-find-a-job-in-finland": {
    title: "LinkedIn Job Search in Finland | How to Use It Effectively | JobCrawls",
    description:
      "LinkedIn covers only a fraction of open roles in Finland. Here is how to use it strategically alongside JobCrawls to search the full Finnish job market and not miss roles posted only on local boards.",
  },

  "/fi/tyoelama/linkedin-tyonhaku-suomessa": {
    title: "LinkedIn-työnhaku Suomessa | Miten käyttää tehokkaasti | JobCrawls",
    description:
      "LinkedIn kattaa vain osan Suomen avoimista työpaikoista. Näin käytät sitä strategisesti yhdessä JobCrawlsin kanssa, jotta katat koko Suomen työmarkkinat etkä jää paitsi paikallisista ilmoituksista.",
  },

  "/sv/arbetsliv/linkedin-jobsok-i-finland": {
    title: "LinkedIn-jobbsök i Finland | Hur du använder det effektivt | JobCrawls",
    description:
      "LinkedIn täcker bara en del av de öppna jobben i Finland. Här är hur du använder det strategiskt tillsammans med JobCrawls för att söka hela den finska arbetsmarknaden.",
  },

  "/en/resources/how-to-write-a-cv-for-a-finnish-employer": {
    title: "CV for a Finnish Employer | Format, Length and What to Include | JobCrawls",
    description:
      "No photo, one page, honest language levels. Finnish CV norms are specific and recruiters notice when you get them wrong. Length, structure, and what to leave out so your application stands out.",
  },

  "/fi/tyoelama/ansioluettelo-suomalaiselle-tyonantajalle": {
    title: "Ansioluettelo suomalaiselle työnantajalle | Rakenne ja vinkit | JobCrawls",
    description:
      "Suomalaiset työnantajat odottavat selkeää, tiivistä ansioluetteloa tietyssä formaatissa. Käymme läpi pituuden, rakenteen, mitä jättää pois ja miten räätälöidä hakemuksesi.",
  },

  "/sv/arbetsliv/cv-for-finsk-arbetsgivare": {
    title: "CV för finsk arbetsgivare | Format, längd och innehåll | JobCrawls",
    description:
      "Finska arbetsgivare förväntar sig ett rent, kortfattat CV i ett specifikt format. Vi går igenom längd, struktur, vad du ska utelämna och hur du skräddarsyr ditt CV för finska arbetsgivare.",
  },

  "/en/resources/finland-job-market-march-2026": {
    title: "Finland Job Market August 2026 | Hiring Trends and Open Roles | JobCrawls",
    description:
      "A data-driven look at Finland's job market in August 2026. Which sectors are growing, where hiring is slowing, and what candidates across Finland are actually competing for.",
  },

  "/fi/tyoelama/suomen-tyomarkkinat-maaliskuu-2026": {
    title: "Suomen työmarkkinat | Alat, palkat ja etätyö | JobCrawls",
    description:
      "Rakentaminen ja ammattityöt johtaa Suomen yli 18 300 aktiivista ilmoitusta elokuussa 2026. Erittely aloittain, palkat kokemustason mukaan ja etätyöosuudet.",
  },

  "/sv/arbetsliv/finlands-arbetsmarknad-mars-2026": {
    title: "Finlands arbetsmarknad | Branscher och löner | JobCrawls",
    description:
      "Bygg & hantverksyrken leder Finlands 18 300+ aktiva annonser i augusti 2026. Full uppdelning per bransch, medianlön per senioritet och distansarbets­frekvenser.",
  },

  "/en/resources/helsinki-job-market-2026": {
    title: "Helsinki Job Market 2026 | Sectors Hiring and Salary Benchmarks | JobCrawls",
    description:
      "Helsinki dominates Finnish hiring. Which sectors are actually growing, what they pay, and where the open roles are, pulled from company career pages and Työmarkkinatori so you don't miss anything.",
  },

  "/fi/tyoelama/helsingin-tyomarkkinat-2026": {
    title: "Helsingin työmarkkinat 2026 | Rekrytoivat alat ja palkat | JobCrawls",
    description:
      "Yksityiskohtainen katsaus Helsingin työmarkkinoihin vuonna 2026. Rekrytoivat toimialat, palkkavertailut keskeisille rooleille ja avoimet työpaikat yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/helsingfors-arbetsmarknad-2026": {
    title: "Helsingfors arbetsmarknad 2026 | Rekryterande sektorer och loner | JobCrawls",
    description:
      "En detaljerad bild av Helsingfors arbetsmarknad 2026. Topprekryterande sektorer, lönebenchmarks för viktiga roller och öppna tjänster från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/tampere-job-market-2026": {
    title: "Tampere Job Market 2026 | Engineering, Tech and What's Hiring | JobCrawls",
    description:
      "Engineering, manufacturing and tech drive Tampere's hiring. We dug through the listings to find what's in demand, what it pays, and where the roles actually live, all in one search.",
  },

  "/fi/tyoelama/tampereen-tyomarkkinat-2026": {
    title: "Tampereen työmarkkinat 2026 | Insinöörityöt, teknologia ja trendit | JobCrawls",
    description:
      "Datapohjainen katsaus Tampereen työmarkkinoihin vuonna 2026. Insinöörityöt, valmistus, teknologia ja terveys kysynnän kohteena. Palkkavertailut ja avoimet paikat yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/tammerfors-arbetsmarknad-2026": {
    title: "Tammerfors arbetsmarknad 2026 | Ingenjör, teknik och trender | JobCrawls",
    description:
      "En datadriven bild av Tammerfors arbetsmarknad 2026. Ingenjör, tillverkning, teknik och vård i efterfrågan. Lönebenchmarks och öppna tjänster aggregerade från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/technology-salary-guide-finland-2026-03": {
    title: "Tech Salary Guide Finland March 2026 | Real Pay Data | JobCrawls",
    description:
      "Salary benchmarks for software engineers, data scientists, DevOps and other tech roles in Finland, based on live job listing data from company career pages and Työmarkkinatori as of March 2026.",
  },

  "/fi/tyoelama/teknologia-alan-palkkaopas-suomi-maaliskuu-2026": {
    title: "Teknologia-alan palkkaopas Suomi maaliskuu 2026 | Oikeat palkat | JobCrawls",
    description:
      "Palkkavertailut ohjelmistokehittäjille, data-analyytikoille, DevOps-insinööreille ja muille teknologia-alan rooleille Suomessa. Perustuu yritysten urasivujen ja Työmarkkinatorin dataan maaliskuussa 2026.",
  },

  "/sv/arbetsliv/teknikloner-finland-mars-2026": {
    title: "Tekniklöner Finland mars 2026 | Verklig lönedata | JobCrawls",
    description:
      "Lönebenchmarks för mjukvaruutvecklare, datavetare, DevOps och andra tekniska roller i Finland, baserade på live-jobbannonser från företags karriärsidor och Työmarkkinatori, mars 2026.",
  },

  "/en/resources/jobs-in-finland/technology-salary-guide-finland-2026-04": {
    title: "Tech Salary Guide Finland April 2026 | Updated Pay Benchmarks | JobCrawls",
    description:
      "Updated salary benchmarks for tech roles in Finland as of April 2026. Software, data, cloud and security roles covered using live data from company career pages and Työmarkkinatori.",
  },

  "/fi/tyoelama/tyot-suomessa/teknologia-palkkaopas-suomi-2026-04": {
    title: "Teknologia-alan palkkaopas Suomi huhtikuu 2026 | Päivitetyt palkat | JobCrawls",
    description:
      "Päivitetyt palkkavertailut teknologia-alan rooleille Suomessa huhtikuussa 2026. Ohjelmistokehitys, data, pilvi ja tietoturva katettu reaaliaikaisella datalla yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/jobb-i-finland/teknologi-loeneguide-finland-2026-04": {
    title: "Tekniklöneguide Finland april 2026 | Uppdaterade lönebenchmarks | JobCrawls",
    description:
      "Uppdaterade lönebenchmarks för tekniska roller i Finland, april 2026. Mjukvara, data, moln och säkerhet täckt med live-data från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/jobs-in-finland/finland-hidden-job-market-networking-2026-03": {
    title: "Finland's Hidden Job Market | Networking and Unlisted Roles | JobCrawls",
    description:
      "Up to half of Finnish jobs are never publicly listed. Here is how to tap into the hidden job market through networking, while using JobCrawls to cover every role that is publicly posted.",
  },

  "/fi/tyoelama/tyot-suomessa/suomen-piilotyomarkkinat-verkostoituminen-2026-03": {
    title: "Suomen piilotyömarkkinat | Verkostoituminen ja julkaisemattomat paikat | JobCrawls",
    description:
      "Jopa puolta suomalaisista työpaikoista ei koskaan julkaista avoimesti. Näin tavoitat piilotyömarkkinat verkostoitumalla samalla kun JobCrawls kattaa kaikki julkisesti ilmoitetut paikat.",
  },

  "/sv/arbetsliv/jobb-i-finland/finlands-dolda-arbetsmarknad-2026-03": {
    title: "Finlands dolda arbetsmarknad | Nätverkande och oannonserade jobb | JobCrawls",
    description:
      "Upp till hälften av finska jobb annonseras aldrig offentligt. Här är hur du kommer åt den dolda arbetsmarknaden via nätverkande medan JobCrawls täcker alla offentligt annonserade roller.",
  },

  "/en/resources/summer-jobs": {
    title: "Summer Jobs in Finland | All in One Search | JobCrawls",
    description:
      "Student roles, seasonal work and part-time positions across Helsinki, Tampere, Turku and beyond. Instead of checking every board separately, see summer jobs from company career pages and Työmarkkinatori in one search.",
  },

  "/fi/tyoelama/kesatyot": {
    title: "Kesätyöt Suomessa | Kaikki kesätyöilmoitukset yhdessä | JobCrawls",
    description:
      "Löydä kesätyöpaikat Suomesta yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Opiskelijapaikat, kausityö ja osa-aikaiset tehtävät Helsingissä, Tampereella, Turussa ja muualla. Päivitetty päivittäin.",
  },

  "/sv/arbetsliv/sommarjobb-i-finland": {
    title: "Sommarjobb i Finland | Allt på ett ställe | JobCrawls",
    description:
      "Hitta sommarjobb i Finland från företags karriärsidor och Työmarkkinatori i en sökning. Studentjobb, säsongsjobb och deltidstjänster i Helsingfors, Tammerfors, Åbo och mer, uppdaterade dagligen.",
  },

  "/en/resources/jobs-in-finland/cool-finnish-tech-companies-english": {
    title: "Finnish Tech Companies That Work in English | Where to Apply | JobCrawls",
    description:
      "You don't need Finnish to work in Finnish tech. Profiles of the best companies hiring in English: what they build, their vibe, and live open roles all in one place instead of hunting each site separately.",
  },

  "/fi/tyoelama/suomalaiset-tech-yritykset-tyokielena-englanti": {
    title: "Suomalaiset tech-yritykset työkielenä englanti | Missä hakea | JobCrawls",
    description:
      "Parhaat suomalaiset teknologiayritykset, jotka rekrytoivat ilman suomen kielen vaatimusta. Yritysprofiilit, mitä ne rakentavat ja linkit avoimiin paikkoihin yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/finska-techbolag-engelska-arbetssprak": {
    title: "Finska techbolag med engelska som arbetsspråk | Var du söker | JobCrawls",
    description:
      "De bästa finska techbolagen som rekryterar utan krav på finska språkkunskaper. Företagsprofiler, vad de bygger och länkar till öppna roller aggregerade från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/jobs-in-finland/highest-salaries-finland": {
    title: "Highest Salaries in Finland 2026: City-by-City Comparison | JobCrawls",
    description:
      "Helsinki, Espoo, Tampere, Turku, and Oulu now sit within a few hundred euros of each other on advertised pay. Compare salaries, rent, and real purchasing power across all five.",
  },

  "/fi/tyoelama/korkeimmat-palkat-suomessa-alueittain": {
    title: "Suomen korkeimmat palkat 2026: kaupunkivertailu | JobCrawls",
    description:
      "Helsinki, Espoo, Tampere, Turku ja Oulu ovat nyt vain muutaman sadan euron päässä toisistaan ilmoitetuissa palkoissa, tyypillisesti 2 707-3 388 €/kk. Vertaa palkkoja, vuokria ja ostovoimaa kaikissa viidessä.",
  },

  "/sv/arbetsliv/hogsta-lonerna-finland-regioner": {
    title: "Finlands högsta löner 2026: stad för stad-jämförelse | JobCrawls",
    description:
      "Helsingfors, Esbo, Tammerfors, Åbo och Uleåborg ligger nu inom några hundra euro från varandra i annonserad lön, oftast 2 707-3 388 €/mån. Jämför löner, hyror och köpkraft i alla fem.",
  },

  "/en/resources/jobs-in-finland/best-job-websites-finland": {
    title: "Best Job Websites in Finland | Compared and Ranked | JobCrawls",
    description:
      "Duunitori, Jobly, Työmarkkinatori, LinkedIn, Indeed, Oikotie: which one actually has the roles you want? We compared them by volume, industry coverage, and which ones are worth your time.",
  },

  "/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa": {
    title: "Parhaat työpaikkasivustot Suomessa | Vertailu ja ranking | JobCrawls",
    description:
      "Kattava vertailu suomalaisista työnhakusivustoista: Duunitori, Jobly, Työmarkkinatori, LinkedIn, Indeed ja muut. Millä sivustoilla on eniten ilmoituksia ja mikä sopii omalle alallesi? Tai käytä vain JobCrawlsia.",
  },

  "/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland": {
    title: "Bästa jobbsajter i Finland | Jämförda och rankade | JobCrawls",
    description:
      "En komplett jämförelse av finska jobbsajter: Duunitori, Jobly, Työmarkkinatori, LinkedIn, Indeed och fler. Vilka sajter har flest annonser och vilken passar bäst för din bransch? Eller använd bara JobCrawls.",
  },
} as const satisfies Record<string, UrlStaticSeoMeta>;
